import colors from './colors';
import sizes from './sizes';
import fonts from './fonts';

// hack: traditional borders aren't rendering correctly
function insetOutline(color) {
    return `0 0 0 1px ${color} inset`;
}

// The below are just to avoid repeating code more than necessary. Just use the final exported object to access styles.

const baseButton = Object.freeze({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0,
    gap: `${sizes.fixedSpacing.md}px`,
    margin: `${sizes.fixedSpacing.md}px`
});

const baseText = Object.freeze({});

const size = Object.freeze({
    nonlink: {
        xs: {
            button: {
                padding: `${sizes.fixedSpacing.xxs}px ${sizes.fixedSpacing.sm}px`,
                borderRadius: sizes.radius.full
            },
            text: {
                ...fonts.text.xs.semiBold
            },
            iconOnly: {
                padding: `${sizes.fixedSpacing.xs}px`,
                borderRadius: sizes.radius.full
            }
        },
        sm: {
            button: {
                padding: `${sizes.fixedSpacing.md}px ${sizes.fixedSpacing.xl}px`,
                borderRadius: sizes.radius.full
            },
            text: {
                ...fonts.text.sm.semiBold
            },
            iconOnly: {
                padding: `${sizes.fixedSpacing.md}px`, // 8px
                borderRadius: sizes.radius.full
            }
        },
        lg: {
            button: {
                padding: `10px ${sizes.fixedSpacing['2xl']}px`,
                borderRadius: sizes.radius.full
            },
            text: {
                ...fonts.text.md.semiBold
            },
            iconOnly: {
                padding: `${sizes.fixedSpacing.md}px`, // 8px
                borderRadius: sizes.radius.full
            }
        }
    },
    link: {
        xs: {
            button: {
                padding: `0px ${sizes.fixedSpacing['2xl']}px`,
                borderRadius: sizes.radius.xs
            },
            text: {
                ...fonts.text.xs.semiBold
            }
        },
        sm: {
            button: {
                padding: `0px ${sizes.fixedSpacing['2xl']}px`,
                borderRadius: sizes.radius.xs
            },
            text: {
                ...fonts.text.sm.semiBold
            }
        },
        lg: {
            button: {
                padding: `0px ${sizes.fixedSpacing['2xl']}px`,
                borderRadius: sizes.radius.xs
            },
            text: {
                ...fonts.text.md.semiBold
            }
        }
    }
});

const color = Object.freeze({
    light: {
        nondestructive: {
            primary: {
                default: {
                    button: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.primary700,
                        borderColor: colors.primary700,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary700,
                        borderColor: colors.primary700,
                        color: colors.white
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.primary200,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary200,
                        color: colors.white
                    }
                }
            },
            secondary: {
                default: {
                    button: {
                        backgroundColor: colors.white,
                        borderColor: colors.coolGray600,
                        color: colors.coolGray800
                    },
                    text: {
                        color: colors.coolGray800
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray100,
                        color: colors.coolGray800
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.coolGray50,
                        borderColor: colors.coolGray800,
                        color: colors.coolGray800
                    },
                    text: {
                        color: colors.coolGray800
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray200,
                        color: colors.coolGray800
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.white,
                        borderColor: colors.coolGray600,
                        color: colors.neutralGray900
                    },
                    text: {
                        color: colors.coolGray800
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray100,
                        color: colors.coolGray800
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.white,
                        borderColor: colors.coolGray300,
                        color: colors.coolGray300
                    },
                    text: {
                        color: colors.coolGray300
                    },
                    dot: {
                        color: colors.coolGray300
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray50,
                        color: colors.coolGray300
                    }
                }
            },
            tertiary: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        borderColor: 'transparent',
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray600
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.primary50,
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray200,
                        color: colors.coolGray800
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray600
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray300
                    },
                    text: {
                        color: colors.coolGray300
                    },
                    dot: {
                        color: colors.coolGray300
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray300
                    }
                }
            },
            link: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                hover: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.primary800
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        borderColor: colors.green500,
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray300
                    },
                    text: {
                        color: colors.coolGray300
                    },
                    dot: {
                        color: colors.coolGray300
                    }
                }
            }
        },
        destructive: {
            primary: {
                default: {
                    button: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red700,
                        borderColor: colors.red700,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red700,
                        borderColor: colors.red700,
                        color: colors.white
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.red200,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red200,
                        color: colors.white
                    }
                }
            },
            secondary: {
                default: {
                    button: {
                        backgroundColor: colors.white,
                        borderColor: colors.red600,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.red50,
                        color: colors.red600
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red50,
                        borderColor: colors.red700,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.red100,
                        color: colors.red700
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.white,
                        borderColor: colors.red600,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.red50,
                        color: colors.red600
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.red25,
                        borderColor: colors.red25,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red300
                    },
                    dot: {
                        color: colors.red300
                    },
                    iconOnly: {
                        backgroundColor: colors.red25,
                        color: colors.red300
                    }
                }
            },
            tertiary: {
                default: {
                    button: {
                        backgroundColor: colors.white,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.white,
                        color: colors.red600
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red50,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.red100,
                        color: colors.red700
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.white,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    },
                    iconOnly: {
                        backgroundColor: colors.white,
                        color: colors.red600
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.white,
                        color: colors.red300
                    },
                    text: {
                        color: colors.red300
                    },
                    dot: {
                        color: colors.red300
                    },
                    iconOnly: {
                        backgroundColor: colors.white,
                        color: colors.red300
                    }
                }
            },
            link: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    }
                },
                hover: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red600
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        borderColor: colors.green500,
                        color: colors.red700
                    },
                    text: {
                        color: colors.red700
                    },
                    dot: {
                        color: colors.red600
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red300
                    },
                    text: {
                        color: colors.red300
                    },
                    dot: {
                        color: colors.red300
                    }
                }
            }
        }
    },
    dark: {
        nondestructive: {
            primary: {
                default: {
                    button: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.primary700,
                        borderColor: colors.primary700,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary700,
                        borderColor: colors.primary700,
                        color: colors.white
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.primary600,
                        borderColor: colors.primary600,
                        color: colors.white
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.primary800,
                        color: colors.primary700
                    },
                    text: {
                        color: colors.primary700
                    },
                    dot: {
                        color: colors.primary700
                    },
                    iconOnly: {
                        backgroundColor: colors.primary800,
                        color: colors.primary700
                    }
                }
            },
            secondary: {
                default: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.coolGray300,
                        color: colors.coolGray50
                    },
                    text: {
                        color: colors.coolGray50
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray700,
                        color: colors.coolGray50
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.coolGray700,
                        borderColor: colors.coolGray25,
                        color: colors.coolGray50
                    },
                    text: {
                        color: colors.coolGray50
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray600,
                        color: colors.coolGray50
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.coolGray300,
                        color: colors.neutralGray50
                    },
                    text: {
                        color: colors.coolGray50
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray700,
                        color: colors.coolGray50
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.coolGray600,
                        color: colors.coolGray600
                    },
                    text: {
                        color: colors.coolGray600
                    },
                    dot: {
                        color: colors.coolGray600
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray700,
                        color: colors.coolGray600
                    }
                }
            },
            tertiary: {
                default: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        color: colors.primary300
                    },
                    text: {
                        color: colors.primary300
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray800,
                        color: colors.coolGray50
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.primary800,
                        color: colors.primary200
                    },
                    text: {
                        color: colors.primary200
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray600,
                        color: colors.coolGray50
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        color: colors.primary300
                    },
                    text: {
                        color: colors.primary300
                    },
                    dot: {
                        color: colors.green500
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray800,
                        color: colors.coolGray50
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        color: colors.coolGray600
                    },
                    text: {
                        color: colors.coolGray600
                    },
                    dot: {
                        color: colors.coolGray600
                    },
                    iconOnly: {
                        backgroundColor: colors.coolGray800,
                        color: colors.coolGray600
                    }
                }
            },
            link: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.primary300
                    },
                    text: {
                        color: colors.primary300
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                hover: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.primary100
                    },
                    text: {
                        color: colors.primary100
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        borderColor: colors.green500,
                        color: colors.primary300
                    },
                    text: {
                        color: colors.primary300
                    },
                    dot: {
                        color: colors.green500
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.coolGray600
                    },
                    text: {
                        color: colors.coolGray600
                    },
                    dot: {
                        color: colors.coolGray600
                    }
                }
            }
        },
        destructive: {
            primary: {
                default: {
                    button: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red600,
                        color: colors.white
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red700,
                        borderColor: colors.red700,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red800,
                        color: colors.white
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.red600,
                        borderColor: colors.red600,
                        color: colors.white
                    },
                    text: {
                        color: colors.white
                    },
                    dot: {
                        color: colors.white
                    },
                    iconOnly: {
                        backgroundColor: colors.red600,
                        color: colors.white
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.red900,
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red800
                    },
                    iconOnly: {
                        backgroundColor: colors.red900,
                        color: colors.red800
                    }
                }
            },
            secondary: {
                default: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.red600,
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    },
                    iconOnly: {
                        backgroundColor: colors.red900,
                        color: colors.red50
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red900,
                        borderColor: colors.red400,
                        color: colors.red200
                    },
                    text: {
                        color: colors.red200
                    },
                    dot: {
                        color: colors.red200
                    },
                    iconOnly: {
                        backgroundColor: colors.red800,
                        color: colors.red50
                    }
                },
                focused: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.red600,
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    },
                    iconOnly: {
                        backgroundColor: colors.red900,
                        color: colors.red50
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: colors.coolGray800,
                        borderColor: colors.red800,
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red800
                    },
                    iconOnly: {
                        backgroundColor: colors.red900,
                        color: colors.red800
                    }
                }
            },
            tertiary: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.red400
                    }
                },
                hover: {
                    button: {
                        backgroundColor: colors.red900,
                        color: colors.red200
                    },
                    text: {
                        color: colors.red200
                    },
                    dot: {
                        color: colors.red200
                    },
                    iconOnly: {
                        backgroundColor: colors.red800,
                        color: colors.red400
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.red400
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red800
                    },
                    iconOnly: {
                        backgroundColor: 'transparent',
                        color: colors.red900
                    }
                }
            },
            link: {
                default: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    }
                },
                hover: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red800
                    }
                },
                focused: {
                    button: {
                        backgroundColor: 'transparent',
                        borderColor: colors.green500,
                        color: colors.red400
                    },
                    text: {
                        color: colors.red400
                    },
                    dot: {
                        color: colors.red400
                    }
                },
                disabled: {
                    button: {
                        backgroundColor: 'transparent',
                        color: colors.red800
                    },
                    text: {
                        color: colors.red800
                    },
                    dot: {
                        color: colors.red800
                    }
                }
            }
        }
    }
});

// The actual exported object to use to access styling:
export default Object.freeze({
    light: {
        nondestructive: {
            primary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.primary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.primary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.primary.disabled.iconOnly
                        }
                    }
                }
            },
            secondary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.secondary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.secondary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.secondary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.secondary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.secondary.default.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.secondary.hover.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.light.nondestructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.secondary.default.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.secondary.hover.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.light.nondestructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.light.nondestructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                }
            },
            tertiary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                }
            },
            link: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.disabled.dot
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.disabled.dot
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.nondestructive.link.disabled.dot
                        }
                    }
                }
            }
        },
        destructive: {
            primary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.primary.default.text
                        },
                        dot: {
                            ...color.light.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.primary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.primary.default.text
                        },
                        dot: {
                            ...color.light.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.primary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.primary.default.text
                        },
                        dot: {
                            ...color.light.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.primary.disabled.iconOnly
                        }
                    }
                }
            },
            secondary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.light.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.secondary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.light.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.secondary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.light.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.light.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.secondary.disabled.iconOnly
                        }
                    }
                }
            },
            tertiary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.light.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.light.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.light.destructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.light.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.light.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.light.destructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.white}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.light.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.light.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.light.destructive.tertiary.disabled.iconOnly
                        }
                    }
                }
            },
            link: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.destructive.link.default.text
                        },
                        dot: {
                            ...color.light.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.destructive.link.hover.text
                        },
                        dot: {
                            ...color.light.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.destructive.link.focused.text
                        },
                        dot: {
                            ...color.light.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.light.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.light.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.link.disabled.dot
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.destructive.link.default.text
                        },
                        dot: {
                            ...color.light.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.destructive.link.hover.text
                        },
                        dot: {
                            ...color.light.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.destructive.link.focused.text
                        },
                        dot: {
                            ...color.light.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.light.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.light.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.link.disabled.dot
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.destructive.link.default.text
                        },
                        dot: {
                            ...color.light.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.destructive.link.hover.text
                        },
                        dot: {
                            ...color.light.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.white}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.destructive.link.focused.text
                        },
                        dot: {
                            ...color.light.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.light.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.light.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.light.destructive.link.disabled.dot
                        }
                    }
                }
            }
        }
    },
    dark: {
        nondestructive: {
            primary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.primary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.primary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.primary.disabled.iconOnly
                        }
                    }
                }
            },
            secondary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.nondestructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.nondestructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.nondestructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.nondestructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.secondary.disabled.iconOnly
                        }
                    }
                }
            },
            tertiary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.nondestructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.nondestructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.nondestructive.tertiary.disabled.iconOnly
                        }
                    }
                }
            },
            link: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.disabled.dot
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.disabled.dot
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.nondestructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.nondestructive.link.default.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.nondestructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.nondestructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.nondestructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.nondestructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.nondestructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.nondestructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.nondestructive.link.disabled.dot
                        }
                    }
                }
            }
        },
        destructive: {
            primary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.primary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.primary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.primary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.primary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.primary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.primary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.primary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.primary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.primary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.primary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.primary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.primary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.primary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.primary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.primary.disabled.iconOnly
                        }
                    }
                }
            },
            secondary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.secondary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.secondary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.secondary.default.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.default.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.secondary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.secondary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.secondary.hover.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.hover.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.secondary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.secondary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.secondary.focused.button,
                            boxShadow: `${insetOutline(color.dark.destructive.secondary.focused.button.borderColor)}, 0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.secondary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.secondary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.secondary.disabled.button,
                            boxShadow: insetOutline(color.dark.destructive.secondary.disabled.button.borderColor)
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.secondary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.secondary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.secondary.disabled.iconOnly
                        }
                    }
                }
            },
            tertiary: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.xs.button,
                            ...color.dark.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.xs.text,
                            ...color.dark.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.xs.iconOnly,
                            ...color.dark.destructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.sm.button,
                            ...color.dark.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.sm.text,
                            ...color.dark.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.sm.iconOnly,
                            ...color.dark.destructive.tertiary.disabled.iconOnly
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.tertiary.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.tertiary.default.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.default.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.tertiary.default.iconOnly
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.tertiary.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.tertiary.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.hover.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.tertiary.hover.iconOnly
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.tertiary.focused.button,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.tertiary.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.focused.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.tertiary.focused.iconOnly,
                            boxShadow: `0 0 0 3px ${colors.coolGray800}, 0 0 0 5px ${colors.green500}`
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.nonlink.lg.button,
                            ...color.dark.destructive.tertiary.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.nonlink.lg.text,
                            ...color.dark.destructive.tertiary.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.tertiary.disabled.dot
                        },
                        iconOnly: {
                            ...baseButton,
                            ...size.nonlink.lg.iconOnly,
                            ...color.dark.destructive.tertiary.disabled.iconOnly
                        }
                    }
                }
            },
            link: {
                xs: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.destructive.link.default.text
                        },
                        dot: {
                            ...color.dark.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.destructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.destructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.xs.button,
                            ...color.dark.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.xs.text,
                            ...color.dark.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.link.disabled.dot
                        }
                    }
                },
                sm: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.destructive.link.default.text
                        },
                        dot: {
                            ...color.dark.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.destructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.destructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.sm.button,
                            ...color.dark.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.sm.text,
                            ...color.dark.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.link.disabled.dot
                        }
                    }
                },
                lg: {
                    default: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.destructive.link.default.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.destructive.link.default.text
                        },
                        dot: {
                            ...color.dark.destructive.link.default.dot
                        }
                    },
                    hover: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.destructive.link.hover.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.destructive.link.hover.text
                        },
                        dot: {
                            ...color.dark.destructive.link.hover.dot
                        }
                    },
                    focused: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.destructive.link.focused.button,
                            boxShadow: `0 0 0 1px ${colors.coolGray800}, 0 0 0 3px ${colors.green500}`
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.destructive.link.focused.text
                        },
                        dot: {
                            ...color.dark.destructive.link.focused.dot
                        }
                    },
                    disabled: {
                        button: {
                            ...baseButton,
                            ...size.link.lg.button,
                            ...color.dark.destructive.link.disabled.button
                        },
                        text: {
                            ...baseText,
                            ...size.link.lg.text,
                            ...color.dark.destructive.link.disabled.text
                        },
                        dot: {
                            ...color.dark.destructive.link.disabled.dot
                        }
                    }
                }
            }
        }
    }
});
